import { Fragment, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { LegacyMark } from "../LegacyMark";
import { OrgSwitcher } from "../OrgSwitcher";
import type { ThemeMode } from "../Tweaks";
import { UserMenu } from "../UserMenu";
import { Badge, Dot, I } from "../ui";
import { useDemoScope } from "../useDemoScope";
import { crumbsForRoute, navItemsFor, pageTitleForRoute } from "./navigation";
import { routeTarget, type Route } from "./routes";

const SIDEBAR_KEY = "legacy.sidebar.collapsed";

function PanelIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M9 4v16" />
      <path d={open ? "m14 9 3 3-3 3" : "m17 9-3 3 3 3"} />
    </svg>
  );
}

export function AppShell({
  children, route, isAdmin, canManageOrg, onlineCount, loading, wsConnected, onGo, theme, onToggleTheme,
}: {
  children: ReactNode;
  theme: ThemeMode;
  onToggleTheme: () => void;
  route: Route;
  isAdmin: boolean;
  canManageOrg: boolean;
  onlineCount: number;
  loading: boolean;
  wsConnected: boolean;
  onGo: (route: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "true"; }
    catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const demoScope = useDemoScope();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pageTitle = pageTitleForRoute(route);
  const crumbs = crumbsForRoute(route);
  const navItems = navItemsFor({ isAdmin, canManageOrg, onlineCount });

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, String(collapsed)); }
    catch { /* La navigazione resta utilizzabile senza storage. */ }
  }, [collapsed]);

  useEffect(() => {
    document.title = `${pageTitle[0]} · Legacy`;
    return () => { document.title = "Legacy"; };
  }, [pageTitle]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (mobileOpen) dialog?.showModal();
    else dialog?.close();
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const query = window.matchMedia("(min-width: 901px)");
    const closeOnDesktop = () => { if (query.matches) setMobileOpen(false); };
    query.addEventListener("change", closeOnDesktop);
    return () => {
      document.body.style.overflow = previousOverflow;
      query.removeEventListener("change", closeOnDesktop);
      dialog?.close();
    };
  }, [mobileOpen]);

  const navigate = (next: string) => {
    if (mobileOpen) {
      dialogRef.current?.close();
      setMobileOpen(false);
      requestAnimationFrame(() => document.getElementById("main-content")?.focus());
    }
    onGo(next);
  };

  const followLink = (event: MouseEvent<HTMLAnchorElement>, next: Route) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(next);
  };

  const sidebarContent = (mobile: boolean) => (
    <>
      <div className="sidebar-heading">
        <a className="brand-app" href="/" aria-label="Legacy, panoramica" onClick={(e) => followLink(e, "dashboard")}>
          <LegacyMark className="sidebar-mark" />
          <span className="brand-copy"><span className="brand-wordmark">Legacy</span><span className="brand-context">Intelligence immobiliare</span></span>
        </a>
        <button
          type="button"
          className="shell-icon-button sidebar-toggle"
          aria-label={mobile ? "Chiudi menu" : collapsed ? "Espandi sidebar" : "Riduci sidebar"}
          title={mobile ? "Chiudi menu" : collapsed ? "Espandi sidebar" : "Riduci sidebar"}
          aria-expanded={mobile || !collapsed}
          aria-controls={mobile ? "mobile-navigation" : "desktop-navigation"}
          onClick={() => mobile ? setMobileOpen(false) : setCollapsed((value) => !value)}
        >
          {mobile ? I.x : <PanelIcon open={collapsed} />}
        </button>
      </div>
      <nav className="nav" aria-label="Navigazione principale" id={mobile ? "mobile-navigation" : "desktop-navigation"}>
        {navItems.map((item) => item.section !== undefined ? (
          <div key={item.id} className="nav-section"><span>{item.section}</span></div>
        ) : (
          <a
            key={item.id}
            href={routeTarget(item.id, {})}
            className={`nav-item${route === item.id ? " active" : ""}`}
            aria-current={route === item.id ? "page" : undefined}
            aria-label={item.label}
            title={collapsed && !mobile ? item.label : undefined}
            onClick={(e) => followLink(e, item.id)}
          >
            <span className="nav-symbol" aria-hidden="true">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
            {typeof item.count === "number" && item.count > 0 && (
              <span className="nav-count" title={`${item.count} estensioni accese`}>{item.count}</span>
            )}
          </a>
        ))}
      </nav>
      <div className="sidebar-bottom">
        <a href="/guida" className={`nav-item${route === "guida" ? " active" : ""}`} aria-label="Guida" aria-current={route === "guida" ? "page" : undefined} title="Guida" onClick={(e) => followLink(e, "guida")}>
          <span className="nav-symbol" aria-hidden="true">{I.help}</span><span className="nav-label">Guida</span>
        </a>
        <UserMenu onGo={navigate} wsConnected={wsConnected} collapsed={collapsed && !mobile} />
      </div>
    </>
  );

  return (
    <div className={`app${collapsed ? " sidebar-collapsed" : ""}`} data-screen-label={route}>
      <a className="skip-link" href="#main-content">Vai al contenuto</a>
      <aside className="sidebar desktop-sidebar" aria-label="Menu Legacy">{sidebarContent(false)}</aside>
      <dialog
        className="mobile-sidebar-dialog"
        ref={dialogRef}
        aria-label="Menu Legacy"
        onClose={() => setMobileOpen(false)}
        onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const controls = [...event.currentTarget.querySelectorAll<HTMLAnchorElement | HTMLButtonElement>("a[href], button:not(:disabled)")].filter((element) => element.getClientRects().length > 0);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
        onClick={(event) => { if (event.target === event.currentTarget) setMobileOpen(false); }}
      >
        <div className="sidebar mobile-sidebar">{sidebarContent(true)}</div>
      </dialog>
      <div className="main">
        <header className="topbar">
          <button type="button" className="shell-icon-button mobile-menu-toggle" aria-label="Apri menu" aria-expanded={mobileOpen} aria-haspopup="dialog" onClick={() => setMobileOpen(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div className="crumbs" aria-label="Posizione corrente">
            {crumbs.map((crumb, index) => (
              <Fragment key={crumb}>
                {index > 0 && <span className="crumb-sep" aria-hidden="true">/</span>}
                {index === crumbs.length - 1 ? <strong>{crumb}</strong> : <span className="crumb-parent">{crumb}</span>}
              </Fragment>
            ))}
          </div>
          <div className="topbar-right">
            {demoScope && <Badge kind="warn" title={`Demo ${demoScope.comune}`}>Demo</Badge>}
            <OrgSwitcher onGo={navigate} />
            <a className="connection-status" href="/estensione" onClick={(e) => followLink(e, "extension")} title={loading ? "Caricamento delle estensioni" : wsConnected ? `${onlineCount} estensioni accese. Aggiornamenti in tempo reale attivi.` : "Aggiornamenti in tempo reale disconnessi. Riconnessione in corso."}>
              <Dot kind={loading || !wsConnected || onlineCount === 0 ? "" : "live"} />
              <span>{loading ? "Caricamento…" : !wsConnected ? "Riconnessione…" : `${onlineCount} ${onlineCount === 1 ? "estensione accesa" : "estensioni accese"}`}</span>
            </a>
            <button
              type="button"
              className="shell-icon-button theme-toggle"
              aria-label={theme === "dark" ? "Tema scuro attivo: passa al chiaro" : "Tema chiaro attivo: passa allo scuro"}
              title={theme === "dark" ? "Tema scuro attivo: passa al chiaro" : "Tema chiaro attivo: passa allo scuro"}
              onClick={onToggleTheme}
            >
              {theme === "dark" ? I.moon : I.sun}
            </button>
          </div>
        </header>
        <main className="page" id="main-content" tabIndex={-1} data-om-validate>
          <div className="page-head"><div><h1 className="page-title">{pageTitle[0]}</h1><p className="page-sub">{pageTitle[1]}</p></div></div>
          {children}
        </main>
      </div>
    </div>
  );
}
